import * as React from "react"
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import './Toast.css'

export type ToastVariant = 'info' | 'success' | 'warning' | 'error'

interface Toast {
  id: string
  variant: ToastVariant
  title: string
  description: string | undefined
}

interface ToastContextValue {
  show: (toast: Omit<Toast, 'id'>) => void
  success: (title: string, description?: string) => void
  error: (title: string, description?: string) => void
  warning: (title: string, description?: string) => void
  info: (title: string, description?: string) => void
}

const Ctx = createContext<ToastContextValue | null>(null)

const TIMEOUT_MS = 5000

export function ToastProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [toasts, setToasts] = useState<Toast[]>([])

  const dismiss = useCallback((id: string) => {
    setToasts((curr) => curr.filter((t) => t.id !== id))
  }, [])

  const show = useCallback(
    (t: Omit<Toast, 'id'>) => {
      const id = crypto.randomUUID()
      setToasts((curr) => [...curr, { ...t, id }])
      setTimeout(() => dismiss(id), TIMEOUT_MS)
    },
    [dismiss],
  )

  const value = useMemo<ToastContextValue>(
    () => ({
      show,
      success: (title, description) => show({ variant: 'success', title, description }),
      error: (title, description) => show({ variant: 'error', title, description }),
      warning: (title, description) => show({ variant: 'warning', title, description }),
      info: (title, description) => show({ variant: 'info', title, description }),
    }),
    [show],
  )

  return (
    <Ctx.Provider value={value}>
      {children}
      <div className="toast-stack" role="region" aria-label="Notifications">
        {toasts.map((t) => (
          <div key={t.id} className={`toast toast-${t.variant}`} role="status">
            <div className="toast-content">
              <strong>{t.title}</strong>
              {t.description ? <span className="toast-desc">{t.description}</span> : null}
            </div>
            <button
              type="button"
              className="toast-close"
              onClick={() => dismiss(t.id)}
              aria-label="Dismiss notification"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </Ctx.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useToast(): ToastContextValue {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useToast must be used within <ToastProvider>')
  return ctx
}
